import * as React from "react";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { 
  Check, 
  X, 
  Eye, 
  Loader2,
  CalendarRange,
  DollarSign,
  Mail,
  Phone,
  Trophy,
  User
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { toast } from '../../../components/ui/use-toast';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { generateSlug } from '../../../lib/utils';
import { Separator } from '../../../components/ui/separator';
import { AdminLayout } from '../../../components/layout/AdminLayout';

// Générateur de code unique pour les concours
const generateUniqueCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

type PendingContest = {
  id: string;
  name: string;
  description: string;
  vote_price: number;
  start_date: string;
  end_date: string;
  created_at: string;
  status: string;
  photo_url: string;
  logo_url: string;
  contact_email: string;
  contact_phone: string;
  submitter_info: {
    name: string;
    organization: string | null;
  };
};

const PendingContests = () => {
  const [pendingContests, setPendingContests] = React.useState<PendingContest[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [processingIds, setProcessingIds] = React.useState<string[]>([]);
  const [selectedContest, setSelectedContest] = React.useState<PendingContest | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  React.useEffect(() => {
    fetchPendingContests();
  }, []);

  const fetchPendingContests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('public_contests_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setPendingContests(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des soumissions de concours:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger les soumissions de concours.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveContest = async (id: string) => {
    try {
      setProcessingIds(prev => [...prev, id]);
      
      // Récupérer les données de la soumission
      const { data: submission, error: fetchError } = await supabase
        .from('public_contests_submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !submission) throw fetchError;

      // Générer un slug et un code unique
      const slug = generateSlug(submission.name);
      const code_unique = generateUniqueCode();

      // Insérer dans la table contests
      const { error: insertError } = await supabase
        .from('contests')
        .insert({
          name: submission.name,
          description: submission.description,
          vote_price: submission.vote_price,
          start_date: submission.start_date,
          end_date: submission.end_date,
          status: 'draft', // Par défaut en brouillon
          slug: slug,
          code_unique: code_unique,
          photo_url: submission.photo_url,
          logo_url: submission.logo_url,
        });

      if (insertError) throw insertError;

      // Mettre à jour le statut de la soumission
      const { error: updateError } = await supabase
        .from('public_contests_submissions')
        .update({ status: 'approved' })
        .eq('id', id);

      if (updateError) throw updateError;

      // Actualiser la liste
      fetchPendingContests();
      
      toast({
        title: 'Concours approuvé',
        description: `Le concours "${submission.name}" a été approuvé et ajouté à la liste des concours.`,
      });
    } catch (error) {
      console.error('Erreur lors de l\'approbation du concours:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'approbation du concours.',
      });
    } finally {
      setProcessingIds(prev => prev.filter(item => item !== id));
    }
  };

  const handleRejectContest = async (id: string) => {
    try {
      setProcessingIds(prev => [...prev, id]);
      
      // Mettre à jour le statut de la soumission
      const { error: updateError } = await supabase
        .from('public_contests_submissions')
        .update({ status: 'rejected' })
        .eq('id', id);

      if (updateError) throw updateError;

      // Actualiser la liste
      fetchPendingContests();
      
      toast({
        title: 'Concours rejeté',
        description: 'La soumission de concours a été rejetée.',
      });
    } catch (error) {
      console.error('Erreur lors du rejet du concours:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors du rejet du concours.',
      });
    } finally {
      setProcessingIds(prev => prev.filter(item => item !== id));
    }
  };

  const handlePreviewContest = (contest: PendingContest) => {
    setSelectedContest(contest);
    setPreviewOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500 text-white">En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-500 text-white">Approuvé</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white">Rejeté</Badge>;
      default:
        return <Badge className="bg-gray-500 text-white">Inconnu</Badge>;
    }
  };

  return (
    <AdminLayout>
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                Soumissions de concours
              </span>
            </h1>
            <p className="text-gray-400 mt-1">
              Gérez les propositions de concours soumises par les utilisateurs
            </p>
          </div>
          <Button
            onClick={fetchPendingContests}
            variant="outline"
            className="bg-white/5 border-white/10 text-white"
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Actualiser
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader2 className="h-10 w-10 animate-spin text-yellow-500" />
          </div>
        ) : pendingContests.length === 0 ? (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Trophy className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">Aucune soumission de concours</h3>
            <p className="text-gray-400">
              Les nouvelles soumissions apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Soumis par</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date de soumission</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {pendingContests.map((contest, index) => (
                    <tr key={contest.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{contest.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{contest.submitter_info?.name || "Non spécifié"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{formatDate(contest.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{getStatusBadge(contest.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreviewContest(contest)}
                            className="text-gray-200 hover:text-white hover:bg-white/10"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                          
                          {contest.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleApproveContest(contest.id)}
                                disabled={processingIds.includes(contest.id)}
                                className="bg-green-600 hover:bg-green-500 text-white"
                              >
                                {processingIds.includes(contest.id) ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="h-4 w-4 mr-1" />
                                    Approuver
                                  </>
                                )}
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleRejectContest(contest.id)}
                                disabled={processingIds.includes(contest.id)}
                              >
                                {processingIds.includes(contest.id) ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <X className="h-4 w-4 mr-1" />
                                    Rejeter
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Dialogue de prévisualisation du concours */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl bg-gray-900 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-white font-bold">Détails de la soumission de concours</DialogTitle>
            <DialogDescription className="text-gray-400">
              Informations complètes sur la proposition de concours
            </DialogDescription>
          </DialogHeader>
          
          {selectedContest && (
            <div className="space-y-6 py-4">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-1/3">
                  {selectedContest.logo_url && (
                    <div className="rounded-lg overflow-hidden bg-white/5 p-4 flex items-center justify-center h-40 border border-white/10">
                      <img 
                        src={selectedContest.logo_url} 
                        alt="Logo du concours" 
                        className="max-h-32 max-w-full object-contain" 
                      />
                    </div>
                  )}
                </div>
                
                <div className="w-full md:w-2/3">
                  <h2 className="text-xl font-bold text-white flex items-center">
                    <Trophy className="h-5 w-5 mr-2 text-yellow-500" />
                    {selectedContest.name}
                  </h2>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="flex items-center text-sm text-gray-300">
                      <CalendarRange className="h-4 w-4 mr-1 text-gray-400" />
                      <span>Début: {formatDate(selectedContest.start_date)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-300">
                      <CalendarRange className="h-4 w-4 mr-1 text-gray-400" />
                      <span>Fin: {formatDate(selectedContest.end_date)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-300">
                      <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                      <span>Prix du vote: {selectedContest.vote_price}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-300">
                      <User className="h-4 w-4 mr-1 text-gray-400" />
                      <span>Statut: {getStatusBadge(selectedContest.status)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-white/10 pt-4"></div>
              
              <div>
                <h3 className="font-semibold mb-2 text-white">Description</h3>
                <ScrollArea className="h-40 rounded-md border border-white/10 p-4 bg-white/5">
                  <p className="text-sm text-gray-300">{selectedContest.description}</p>
                </ScrollArea>
              </div>
              
              {selectedContest.photo_url && (
                <div>
                  <h3 className="font-semibold mb-2 text-white">Image de couverture</h3>
                  <div className="rounded-md overflow-hidden border border-white/10">
                    <img 
                      src={selectedContest.photo_url} 
                      alt="Image de couverture" 
                      className="w-full h-40 object-cover" 
                    />
                  </div>
                </div>
              )}
              
              <div className="border-t border-white/10 pt-4"></div>

              <div>
                <h3 className="font-semibold mb-3 text-white">Coordonnées du soumissionnaire</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                    <div className="flex items-center text-gray-200 mb-1">
                      <User className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="font-medium">Nom</span>
                    </div>
                    <p className="ml-6 text-gray-300">{selectedContest.submitter_info?.name || "Non spécifié"}</p>
                  </div>
                  
                  {selectedContest.submitter_info?.organization && (
                    <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                      <div className="flex items-center text-gray-200 mb-1">
                        <Trophy className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="font-medium">Organisation</span>
                      </div>
                      <p className="ml-6 text-gray-300">{selectedContest.submitter_info.organization}</p>
                    </div>
                  )}
                  
                  <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                    <div className="flex items-center text-gray-200 mb-1">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="font-medium">Email</span>
                    </div>
                    <p className="ml-6 text-gray-300">{selectedContest.contact_email}</p>
                  </div>
                  
                  <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                    <div className="flex items-center text-gray-200 mb-1">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="font-medium">Téléphone</span>
                    </div>
                    <p className="ml-6 text-gray-300">{selectedContest.contact_phone}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            {selectedContest && selectedContest.status === 'pending' && (
              <>
                <Button
                  onClick={() => {
                    handleRejectContest(selectedContest.id);
                    setPreviewOpen(false);
                  }}
                  variant="destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  onClick={() => {
                    handleApproveContest(selectedContest.id);
                    setPreviewOpen(false);
                  }}
                  variant="default"
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approuver
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PendingContests;
